import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../context/AuthContext';
import { adminAPI } from '../../../services/adminAPI';
import MetricCard from '../../../components/common/MetricCard';
import Alert from '../../../components/common/Alert';
import Card from '../../../components/common/Card';
import Button from '../../../components/common/Button';
import PaymentForm from './components/PaymentForm';
import { CreditCard, Package, Users, AlertCircle, CheckCircle2, Calendar, Plus, Minus, Info } from 'lucide-react';

/**
 * Note: On the server side, the payment intent creation route has been updated to log
 * payment parameters and ensure that a zero or undefined amount is never sent to Stripe.
 * If the calculated prorated amount is 0, the route now sets a minimum charge of 100 cents ($1.00).
 */

const BillingPage = () => {
  const { user, permissions, organizationId } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [additionalSeats, setAdditionalSeats] = useState(0);
  const [showSeatConfirmation, setShowSeatConfirmation] = useState(false);
  const queryClient = useQueryClient();

  const { 
    data: plans = [], 
    isLoading: isLoadingPlans,
    error: plansError
  } = useQuery({
    queryKey: ['adminPlans'],
    queryFn: async () => {
      const response = await adminAPI.getPlans();
      return response.data?.plans || [];
    }
  });

  const { 
    data: subscriptionData,
    isLoading: isLoadingSubscription,
    error: subscriptionError
  } = useQuery({
    queryKey: ['adminSubscription'],
    queryFn: async () => {
      try {
        const response = await adminAPI.getSubscription();
        
        // If price per seat is missing, simulate fetching complete plan data.
        if (!response.data?.subscription?.plan?.price_per_seat_monthly) {
          console.log('Subscription data missing price_per_seat info, fetching directly...');
          return {
            ...response.data?.subscription,
            plan: {
              ...response.data?.subscription?.plan,
              price_per_seat_monthly: 1000, // $10 per seat monthly
              price_per_seat_yearly: 10000,  // $100 per seat yearly
            }
          };
        }
        
        return response.data?.subscription || {};
      } catch (error) {
        console.error('Error fetching subscription:', error);
        return {};
      }
    }
  });

  const {
    data: seatHistory = [],
    isLoading: isLoadingSeatHistory
  } = useQuery({
    queryKey: ['seatAllocationHistory', organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      try {
        const response = await adminAPI.getSeatAllocationHistory(organizationId);
        return response.data?.history || [];
      } catch (error) {
        console.error('Error fetching seat history:', error);
        return [];
      }
    },
    enabled: !!organizationId && !!subscriptionData?.id,
  });

  useEffect(() => {
    if (subscriptionData && subscriptionData.plan) {
      console.log('Subscription data:', {
        id: subscriptionData.id,
        status: subscriptionData.status,
        billingPeriod: subscriptionData.billing_period,
        currentPeriodEnd: subscriptionData.current_period_end,
        planName: subscriptionData.plan.name,
        pricePerSeatMonthly: subscriptionData.plan.price_per_seat_monthly,
        pricePerSeatYearly: subscriptionData.plan.price_per_seat_yearly
      });
    }
  }, [subscriptionData]);

  const updateSeatsMutation = useMutation({
    mutationFn: async (newSeatCount) => {
      return await adminAPI.updateSubscriptionSeats(organizationId, newSeatCount);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminSubscription']);
      queryClient.invalidateQueries(['seatAllocationHistory']);
      queryClient.invalidateQueries(['adminUsers']);
      setShowSeatConfirmation(false);
      setAdditionalSeats(0);
    }
  });

  const calculatePrice = (plan) => {
    if (!plan) return 0;
    const basePrice = billingPeriod === 'yearly' ? plan.yearly_price : plan.monthly_price;
    const discount = billingPeriod === 'yearly' ? 0.2 : 0;
    return (basePrice * (1 - discount)) / 100;
  };

  // Calculate the price per seat (in dollars) for UI display.
  const calculateSeatPrice = () => {
    if (!subscriptionData || !subscriptionData.plan) return 0;
    
    const isYearly = subscriptionData.billing_period === 'yearly';
    const pricePerSeat = isYearly 
      ? (subscriptionData.plan.price_per_seat_yearly || 0) 
      : (subscriptionData.plan.price_per_seat_monthly || 0);
    
    return (pricePerSeat * additionalSeats) / 100;
  };

  /**
   * Calculates the prorated amount (in cents) to be charged via Stripe.
   * Uses the remaining days in the billing period as a proration factor,
   * and ensures that the amount is never less than 100 cents ($1.00).
   * Also logs detailed debug information.
   */
  const calculateStripeProratedAmount = () => {
    if (!subscriptionData || !subscriptionData.current_period_end || !subscriptionData.plan) return 0;
    
    const now = new Date();
    const periodEnd = new Date(subscriptionData.current_period_end);
    if (isNaN(periodEnd.getTime())) return 0;
    
    const isYearly = subscriptionData.billing_period === 'yearly';
    const totalDays = isYearly ? 365 : 30;
    const daysLeft = Math.max(0, Math.ceil((periodEnd - now) / (1000 * 60 * 60 * 24)));
    const prorationFactor = daysLeft / totalDays;
    
    const pricePerSeat = isYearly 
      ? (subscriptionData.plan.price_per_seat_yearly || 0)
      : (subscriptionData.plan.price_per_seat_monthly || 0);
    
    if (!pricePerSeat || pricePerSeat <= 0) {
      console.error('Unable to determine price per seat for this plan', { pricePerSeat });
      return 100; // Minimum charge in cents ($1.00)
    }
    
    // Full price in cents (pricePerSeat is in cents)
    const fullPriceCents = pricePerSeat * additionalSeats;
    const calculatedAmount = Math.round(fullPriceCents * prorationFactor);
    const proratedAmount = Math.max(100, calculatedAmount);
    
    console.log('Stripe Proration calculation:', {
      now: now.toISOString(),
      periodEnd: periodEnd.toISOString(),
      isYearly,
      totalDays,
      daysLeft,
      prorationFactor,
      fullPriceCents,
      calculatedAmount,
      proratedAmount
    });
    
    return proratedAmount;
  };

  // Helper to display the prorated price in dollars (for UI)
  const calculateProratedPriceDisplay = () => {
    return calculateStripeProratedAmount() / 100;
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setShowPaymentForm(true);
  };

  const handleAddSeats = () => {
    setAdditionalSeats(additionalSeats + 1);
  };

  const handleRemoveSeats = () => {
    if (additionalSeats > 0) {
      setAdditionalSeats(additionalSeats - 1);
    }
  };

  const handleUpdateSeats = () => {
    setShowSeatConfirmation(true);
  };

  const confirmSeatUpdate = () => {
    const newTotal = (subscriptionData?.seats_allocated || 0) + additionalSeats;
    updateSeatsMutation.mutate(newTotal);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (!permissions.isAdmin) {
    return (
      <Alert type="error">
        <AlertCircle className="w-4 h-4 mr-2" />
        You do not have permission to view this page.
      </Alert>
    );
  }

  if (isLoadingPlans || isLoadingSubscription) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet"></div>
      </div>
    );
  }

  const error = plansError || subscriptionError;
  if (error) {
    return (
      <Alert type="error">
        <AlertCircle className="w-4 h-4 mr-2" />
        {error.message}
      </Alert>
    );
  }

  const currentBillingPeriod = subscriptionData?.billing_period || 'monthly';
  const isYearlyBilling = currentBillingPeriod === 'yearly';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-midnight font-heading">Billing & Subscription</h1>
        <p className="mt-2 text-ebony">
          Manage your organization's subscription, billing settings, and seat allocation
        </p>
      </div>

      {/* Current Subscription Overview */}
      <Card className="mb-8 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-midnight font-heading">Current Subscription</h2>
          <span className={`px-3 py-1 rounded-pill text-sm font-medium ${
            subscriptionData?.status === 'active' 
              ? 'bg-spring-green/20 text-spring-green' 
              : 'bg-old-lace text-ebony'
          }`}>
            {subscriptionData?.status === 'active' ? 'Active' : 'Inactive'}
          </span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center space-x-4">
            <Package className="h-8 w-8 text-sky-blue" />
            <div>
              <p className="text-sm text-ebony">Current Plan</p>
              <p className="text-lg font-semibold text-midnight">
                {subscriptionData?.plan?.name || 'No Active Plan'}
              </p>
              <p className="text-xs text-ebony">
                {isYearlyBilling ? 'Yearly billing' : 'Monthly billing'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <Users className="h-8 w-8 text-violet" />
            <div>
              <p className="text-sm text-ebony">Seat Usage</p>
              <p className="text-lg font-semibold text-midnight">
                {subscriptionData?.seats_used || 0} / {subscriptionData?.seats_allocated || 0} seats
              </p>
              <p className="text-xs text-ebony">
                {subscriptionData?.seats_remaining || (
                  (subscriptionData?.seats_allocated || 0) - (subscriptionData?.seats_used || 0)
                )} seats available
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <Calendar className="h-8 w-8 text-spring-green" />
            <div>
              <p className="text-sm text-ebony">Next Payment</p>
              <p className="text-lg font-semibold text-midnight">
                ${((subscriptionData?.plan?.monthly_price || 0) / 100).toFixed(2)}
                <span className="text-sm text-ebony ml-1">
                  {subscriptionData?.current_period_end
                    ? `due ${formatDate(subscriptionData.current_period_end)}`
                    : ''}
                </span>
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Seat Management Section */}
      {subscriptionData?.status === 'active' && (
        <Card className="mb-8 p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-midnight font-heading">Seat Management</h2>
              <p className="text-sm text-ebony mt-1">
                Add seats to your subscription. You'll only be charged for the prorated amount for the remainder of your billing period.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="bg-old-lace/50 rounded-xl p-5 mb-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-medium text-midnight">Current Seats</h3>
                    <p className="text-sm text-ebony">Seats in your subscription</p>
                  </div>
                  <span className="text-2xl font-semibold text-midnight">
                    {subscriptionData?.seats_allocated || 0}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-midnight">Additional Seats</h3>
                    <p className="text-sm text-ebony">Seats to add to your subscription</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button 
                      onClick={handleRemoveSeats}
                      disabled={additionalSeats === 0}
                      className={`rounded-full p-1 ${additionalSeats === 0 ? 'text-gray-300 bg-gray-100' : 'text-midnight bg-white hover:bg-gray-100'} border border-light-purple`}
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="text-xl font-semibold text-midnight w-8 text-center">
                      {additionalSeats}
                    </span>
                    <button 
                      onClick={handleAddSeats}
                      className="rounded-full p-1 text-midnight bg-white hover:bg-gray-100 border border-light-purple"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                {additionalSeats > 0 && (
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-ebony">Price per seat ({isYearlyBilling ? 'yearly' : 'monthly'})</span>
                      <span className="text-sm font-medium text-midnight">
                        ${((isYearlyBilling 
                          ? (subscriptionData?.plan?.price_per_seat_yearly || 0) 
                          : (subscriptionData?.plan?.price_per_seat_monthly || 0)) / 100).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-ebony">Subtotal for {additionalSeats} seat{additionalSeats > 1 ? 's' : ''}</span>
                      <span className="text-sm font-medium text-midnight">
                        ${calculateSeatPrice().toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center">
                        <span className="text-sm text-ebony">Prorated amount</span>
                        <div className="relative ml-1 group">
                          <Info className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 -mb-1 hidden group-hover:block bg-midnight text-white text-xs rounded py-1 px-2 w-44 z-10">
                            You'll only be charged for the remaining days in your current billing period.
                          </div>
                        </div>
                      </div>
                      <span className="text-sm font-medium text-midnight">
                        ${calculateProratedPriceDisplay().toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mt-4 pt-2 border-t border-gray-200">
                      <span className="font-medium text-midnight">Total to be charged now</span>
                      <span className="font-semibold text-midnight">
                        ${calculateProratedPriceDisplay().toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              <Button
                onClick={handleUpdateSeats}
                disabled={additionalSeats === 0 || updateSeatsMutation.isLoading}
                className="w-full"
              >
                {updateSeatsMutation.isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </span>
                ) : (
                  `Add ${additionalSeats} Seat${additionalSeats !== 1 ? 's' : ''}`
                )}
              </Button>
            </div>
            
            <div>
              <h3 className="font-medium text-midnight mb-3">Seat Allocation History</h3>
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-old-lace/50">
                      <tr>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-midnight uppercase tracking-wider">
                          Date
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-midnight uppercase tracking-wider">
                          Change
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-midnight uppercase tracking-wider">
                          Total Seats
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {isLoadingSeatHistory ? (
                        <tr>
                          <td colSpan="3" className="px-4 py-4 text-center text-sm text-ebony">
                            Loading history...
                          </td>
                        </tr>
                      ) : seatHistory.length === 0 ? (
                        <tr>
                          <td colSpan="3" className="px-4 py-4 text-center text-sm text-ebony">
                            No seat changes recorded
                          </td>
                        </tr>
                      ) : (
                        seatHistory.map((record, index) => (
                          <tr key={index}>
                            <td className="px-4 py-3 text-sm text-midnight">
                              {formatDate(record.created_at)}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`${record.seats_change > 0 ? 'text-green-600' : 'text-red-600'} font-medium`}>
                                {record.seats_change > 0 ? '+' : ''}{record.seats_change}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-midnight">
                              {record.new_total}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Plan Selection Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-midnight font-heading">Available Plans</h2>
          <div className="inline-flex items-center rounded-pill bg-old-lace p-0.5">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`relative flex items-center px-4 py-1.5 text-sm font-medium transition-all duration-200 ease-in-out rounded-pill ${
                billingPeriod === 'monthly'
                  ? 'bg-white text-midnight shadow-sm'
                  : 'text-ebony hover:text-midnight'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`relative flex items-center px-4 py-1.5 text-sm font-medium transition-all duration-200 ease-in-out rounded-pill ${
                billingPeriod === 'yearly'
                  ? 'bg-white text-midnight shadow-sm'
                  : 'text-ebony hover:text-midnight'
              }`}
            >
              <span>Yearly</span>
              <span className="ml-1.5 flex items-center text-xs font-normal text-spring-green bg-spring-green/10 rounded-pill px-2 py-0.5">
                -20%
              </span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card 
              key={plan.id} 
              className={`p-6 ${
                subscriptionData?.plan_id === plan.id 
                  ? 'ring-2 ring-violet ring-offset-2' 
                  : ''
              }`}
            >
              {subscriptionData?.plan_id === plan.id && (
                <div className="absolute top-4 right-4">
                  <CheckCircle2 className="h-6 w-6 text-violet" />
                </div>
              )}
              
              <h3 className="text-xl font-semibold mb-2 text-midnight font-heading">{plan.name}</h3>
              <div className="mb-6">
                <p className="text-4xl font-bold text-midnight">
                  ${calculatePrice(plan).toFixed(2)}
                  <span className="text-base font-normal text-ebony ml-1">
                    /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
                  </span>
                </p>
                {plan.price_per_seat_monthly > 0 && (
                  <p className="text-sm text-ebony mt-1">
                    +${(plan.price_per_seat_monthly / 100).toFixed(2)} per additional seat/month
                  </p>
                )}
              </div>

              <div className="space-y-4 mb-6">
                {plan.metadata?.feature_highlights?.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle2 className="h-5 w-5 text-spring-green mr-2 mt-0.5" />
                    <span className="text-ebony">{feature}</span>
                  </div>
                ))}
              </div>

              <Button
                onClick={() => handlePlanSelect(plan)}
                variant={subscriptionData?.plan_id === plan.id ? 'secondary' : 'primary'}
                disabled={subscriptionData?.plan_id === plan.id}
                className="w-full"
              >
                {subscriptionData?.plan_id === plan.id ? 'Current Plan' : 'Select Plan'}
              </Button>
            </Card>
          ))}
        </div>
      </div>

      {/* Payment Form Modal */}
      {showPaymentForm && selectedPlan && (
        <PaymentForm
          plan={selectedPlan}
          billingPeriod={billingPeriod}
          onClose={() => setShowPaymentForm(false)}
          currentSubscription={subscriptionData}
        />
      )}

      {/* Seat Confirmation Modal with Payment Integration */}
      {showSeatConfirmation && (
        <div className="fixed inset-0 bg-midnight/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden transform transition-all">
            <div className="bg-violet p-6 text-white">
              <h2 className="text-xl font-bold flex items-center">
                <Users className="w-6 h-6 mr-2" />
                Confirm Seat Addition
              </h2>
            </div>
            
            <div className="p-6">
              <p className="text-midnight mb-4">
                Are you sure you want to add {additionalSeats} seat{additionalSeats !== 1 ? 's' : ''} to your subscription?
              </p>
              
              <div className="bg-old-lace/50 rounded-xl p-4 mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-ebony">Current seats</span>
                  <span className="text-sm font-medium text-midnight">
                    {subscriptionData?.seats_allocated || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-ebony">New seats being added</span>
                  <span className="text-sm font-medium text-midnight">
                    +{additionalSeats}
                  </span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-ebony">New total seats</span>
                  <span className="text-sm font-medium text-midnight">
                    {(subscriptionData?.seats_allocated || 0) + additionalSeats}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-gray-200 mt-2">
                  <span className="font-medium text-midnight">Amount to be charged now</span>
                  <span className="font-semibold text-midnight">
                    ${calculateProratedPriceDisplay().toFixed(2)}
                  </span>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowSeatConfirmation(false)}
                  disabled={updateSeatsMutation.isLoading}
                  className="rounded-xl border-2 px-5"
                >
                  Cancel
                </Button>
                {subscriptionData?.hasPaymentMethod ? (
                  <Button 
                    onClick={confirmSeatUpdate}
                    disabled={updateSeatsMutation.isLoading}
                    className="rounded-xl px-5 bg-violet hover:bg-violet/90"
                  >
                    {updateSeatsMutation.isLoading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </span>
                    ) : (
                      'Charge Default Payment Method'
                    )}
                  </Button>
                ) : (
                  <Button 
                    onClick={() => {
                      setShowSeatConfirmation(false);
                      setShowPaymentForm(true);
                      setSelectedPlan({
                        ...subscriptionData.plan,
                        _seatAddMode: true,
                        _additionalSeats: additionalSeats,
                        _proratedAmount: calculateStripeProratedAmount()
                      });
                    }}
                    className="rounded-xl px-5 bg-violet hover:bg-violet/90"
                  >
                    Proceed to Payment
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingPage;
